import { apiDelete, apiGet, apiPost, apiPut } from "../client";
import { graphqlRequest } from "../graphqlClient";
import type {
	CreateScenarioAdjustmentRequestApi,
	ScenarioAdjustmentListResponseApi,
	ScenarioAdjustmentResponseApi,
	ScenarioAdjustmentViewNode,
	ScenarioAdjustmentViewTotals,
	UpdateScenarioAdjustmentRequestApi,
} from "../models/scenarioAdjustment";

// ─── REST CRUD ───────────────────────────────────────────────────────────────

export async function getScenarioAdjustments(
	scenarioId: number,
): Promise<ScenarioAdjustmentListResponseApi> {
	return apiGet<ScenarioAdjustmentListResponseApi>(
		`scenarios/${scenarioId}/adjustments`,
	);
}

export async function getScenarioAdjustmentById(
	scenarioId: number,
	adjustmentId: number,
): Promise<ScenarioAdjustmentResponseApi> {
	return apiGet<ScenarioAdjustmentResponseApi>(
		`scenarios/${scenarioId}/adjustments/${adjustmentId}`,
	);
}

export async function createScenarioAdjustment(
	scenarioId: number,
	request: CreateScenarioAdjustmentRequestApi,
): Promise<ScenarioAdjustmentResponseApi> {
	return apiPost<ScenarioAdjustmentResponseApi>(
		`scenarios/${scenarioId}/adjustments`,
		request,
	);
}

export async function updateScenarioAdjustment(
	scenarioId: number,
	adjustmentId: number,
	request: UpdateScenarioAdjustmentRequestApi,
): Promise<ScenarioAdjustmentResponseApi> {
	return apiPut<ScenarioAdjustmentResponseApi>(
		`scenarios/${scenarioId}/adjustments/${adjustmentId}`,
		request,
	);
}

export async function deleteScenarioAdjustment(
	scenarioId: number,
	adjustmentId: number,
): Promise<void> {
	return apiDelete<void>(
		`scenarios/${scenarioId}/adjustments/${adjustmentId}`,
	);
}

// ─── GraphQL Queries ─────────────────────────────────────────────────────────

interface ConnectionEdge<T> {
	cursor: string;
	node: T;
}

interface PageInfo {
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	startCursor: string | null;
	endCursor: string | null;
}

interface Connection<T> {
	edges: ConnectionEdge<T>[];
	pageInfo: PageInfo;
}

interface ScenarioAdjustmentViewListResponse {
	scenarioAdjustments: {
		scenarioAdjustmentViewList: Connection<ScenarioAdjustmentViewNode>;
	};
}

export async function getScenarioAdjustmentsGraphql(
	scenarioId: number,
	first?: number,
	after?: string,
): Promise<Connection<ScenarioAdjustmentViewNode>> {
	const query = `
    query ScenarioAdjustmentViewList($filter: ScenarioAdjustmentViewFilterInput, $first: Int, $after: String) {
      scenarioAdjustments {
        scenarioAdjustmentViewList(filter: $filter, first: $first, after: $after) {
          edges {
            cursor
            node {
              id
              scenarioId
              adjustmentType
              adjustmentDirection
              startDate
              endDate
              frequency
              confidenceLevel
              description
              additionalComments
              adjustmentValuePercentage
              adjustedValue
              adjustedDueDate
              linkedTransactionId
              transactionType
              monthEndHandling
              netCashImpact
              linkedAccountName
              linkedTransactionSource
              linkedTransactionAmount
              linkedTransactionDueDate
              linkedTransactionStatus
              createdByUserId
              createdByUserName
              dateCreated
              dateModified
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
	const filter = { scenarioId: { eq: scenarioId } };
	const data =
		await graphqlRequest<ScenarioAdjustmentViewListResponse>(query, {
			filter,
			first,
			after,
		});
	return data.scenarioAdjustments.scenarioAdjustmentViewList;
}

interface ScenarioAdjustmentViewTotalsResponse {
	scenarioAdjustments: {
		scenarioAdjustmentViewTotals: ScenarioAdjustmentViewTotals;
	};
}

export async function getScenarioAdjustmentTotals(
	scenarioId: number,
): Promise<ScenarioAdjustmentViewTotals> {
	const query = `
    query ScenarioAdjustmentViewTotals($scenarioId: Int!) {
      scenarioAdjustments {
        scenarioAdjustmentViewTotals(scenarioId: $scenarioId) {
          revenueCount
          revenueSum
          revenueNetCashImpact
          costCount
          costSum
          costNetCashImpact
        }
      }
    }
  `;
	const data =
		await graphqlRequest<ScenarioAdjustmentViewTotalsResponse>(query, {
			scenarioId,
		});
	return data.scenarioAdjustments.scenarioAdjustmentViewTotals;
}
